/* eslint-disable max-nested-callbacks */
/* eslint-disable camelcase */
import './theme/main.scss';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import AppRoutes from './AppRoutes';
import { MainTemplate } from 'Components/templates';
import {
    PAGE_ROUTE,
    SIGN_IN,
    TENANTS_ROUTE,
    NOT_FOUND_ROUTE,
    REDIRECT_ROUTE,
    VIDEO_PLAYER_ROUTE,
} from 'Core-utils/routes';
import { AuthRedirectPage, SignIn, VideoPlayerPage } from 'Components/pages';
import { ProtectedRoute } from 'Core-utils/ProtectedRoutes';
import { SWRConfig } from 'swr';
import { NotFoundPage } from 'Components/pages';
import { TenantSelectionPage } from 'Components/pages';
import { ContentfulContext } from 'Rdx/contexts/contentfulContext';
import { useEffect, useReducer, useState } from 'react';
import { RootState } from 'Rdx/store';
import { ToastsContext, ToastsDispatchContext } from 'Rdx/contexts/ToastsContext';
import ToastType from 'Core-utils/types/ToastType';
import { MdVerified } from '@react-icons/all-files/md/MdVerified';
import { MdOutlineError } from '@react-icons/all-files/md/MdOutlineError';
import { PiWarningFill } from '@react-icons/all-files/pi/PiWarningFill';
import { SeverityType } from 'Core-utils/types/types';
import { DEFAULT_ERROR_MSG, ERROR_VARIANT, TOAST_SHOW_DURATION } from 'Core-utils/constants/constants';
import { Toaster } from 'Components/molecules/Toaster';
import { AmpyChatProvider } from 'Rdx/contexts/AmpyChatContext';
import { useSelector } from 'react-redux';
import { fetchAuthenticatedContentfulEntriesCached } from 'Apis/contentfulProxy';

let nextToastId = 0;
type ToastReducerType =
    | {
          type: 'add';
          payload: Omit<ToastType, 'id'>;
      }
    | {
          type: 'delete';
          payload: {
              id: number;
          };
      }
    | any;

const toastsReducer = (toasts: ToastType[], action: ToastReducerType) => {
    switch (action.type) {
        case 'add': {
            return [...toasts, action.payload];
        }
        case 'delete': {
            return toasts.filter((toast) => toast.id !== action.payload.id);
        }
        default: {
            throw new Error('Unknown action type: ' + action);
        }
    }
};

const App = () => {
    const { tenants, token } = useSelector((state: RootState) => state.auth);
    const [contentfulData, setContentfulData] = useState({});
    const [toasts, dispatch] = useReducer(toastsReducer, []);
    const hasBanner = useSelector((state: RootState) => state.featureModel.enableBanner);

    const getSnackBarBody = (toast: Pick<ToastType, 'severity' | 'body'>) => {
        if (toast.severity === ERROR_VARIANT && process.env.NODE_ENV !== 'development') {
            return DEFAULT_ERROR_MSG;
        }
        return toast.body;
    };

    const getSnackBarProperties = (severity: SeverityType) => {
        switch (severity) {
            case 'success':
                return {
                    borderColor: 'success-green',
                    icon: <MdVerified className="text-success-green" />,
                };
            case 'error':
                return {
                    borderColor: 'secondary-accent-red-4',
                    icon: <MdOutlineError className="text-secondary-accent-red-4" />,
                };
            case 'warning':
                return {
                    borderColor: 'secondary-accent-yellow-3',
                    icon: <PiWarningFill className="text-secondary-accent-yellow-3" />,
                };
            case 'info':
                return {
                    borderColor: 'primary-500',
                    icon: <MdOutlineError className="text-primary-500" />,
                };
            default:
                return {
                    borderColor: '',
                    icon: null,
                };
        }
    };
    const handleAddToast = (partialToast: Omit<ToastType, 'id' | 'borderColor' | 'icon'>) => {
        const newToastId = nextToastId++;
        const newToast = {
            ...partialToast,
            ...getSnackBarProperties(partialToast.severity),
            body: getSnackBarBody(partialToast),
            showDuration: partialToast.showDuration || TOAST_SHOW_DURATION.DEFAULT,
            id: newToastId,
        };
        dispatch({
            type: 'add',
            payload: newToast,
        });
        setTimeout(() => {
            dispatch({
                type: 'delete',
                payload: {
                    id: newToastId,
                },
            });
        }, newToast.showDuration);
    };
    const handleDeleteToast = (id: number) => {
        dispatch({
            type: 'delete',
            payload: {
                id,
            },
        });
    };

    const mapEntries = (contentfulReponse: any) => {
        const entriesMap: any = {};
        contentfulReponse?.includes?.Entry?.forEach((entry: any) => {
            entriesMap[entry?.sys?.id] = entry?.fields;
        });
        return entriesMap;
    };

    const mapContentfulData = (data: string, key: string, contentfulReponse: any, entriesMap: any) => {
        const mappedData: any = {};
        contentfulReponse.items[0]?.fields[data]?.forEach((item: any) => {
            mappedData[entriesMap[item?.sys?.id][key]] = entriesMap[item?.sys?.id];
        });
        return mappedData;
    };

    useEffect(() => {
        if (!token || !(tenants?.length > 0 && tenants[0]?.id)) {
            return;
        }

        fetchAuthenticatedContentfulEntriesCached(
            { content_type: 'tenantData', 'fields.tenantId': tenants[0]?.id, include: 10 },
            token,
        ).then((response: any) => {
            const entriesMap = mapEntries(response);
            const findings = mapContentfulData('findings', 'kind', response, entriesMap);
            const tenantData = {
                ...response?.items[0]?.fields,
                findings,
            };
            setContentfulData((data: any) => ({
                ...data,
                ...tenantData,
            }));
        });

        fetchAuthenticatedContentfulEntriesCached({ content_type: 'integration' }, token).then((response: any) => {
            const mappedItems: Record<string, any> = {};
            const providerKeyMap: Record<string, any> = {};
            const integrationFindings: { integration: any; findingType: any }[] = [];

            response?.items.forEach((item: any) => {
                if (!item?.fields?.name || !item?.fields?.providerKey) {
                    return;
                }
                mappedItems[item?.fields?.name] = item.fields;
                providerKeyMap[item?.fields?.providerKey] = item?.fields;

                const findings = mappedItems[item?.fields?.name]?.findings;
                if (findings?.length > 0) {
                    const mappedFindings = findings
                        .filter((finding: any) => !!finding?.fields)
                        .map((finding: any) => {
                            integrationFindings.push({ integration: item?.fields, findingType: finding?.fields });
                            return { ...finding?.fields };
                        });
                    mappedItems[item?.fields?.name].findings = mappedFindings;
                    providerKeyMap[item?.fields?.providerKey]['findings'] = mappedFindings;
                }
            });
            setContentfulData((data: any) => ({
                ...data,
                integrations: mappedItems,
                providerNameIntegrationMap: providerKeyMap,
                integrationFindings: integrationFindings,
            }));
        });
    }, [tenants, token]);

    return (
        <SWRConfig value={{ provider: () => new Map() }}>
            <BrowserRouter>
                <div className="App d-flex">
                    <Routes>
                        <Route path={SIGN_IN} element={<SignIn />} />
                        <Route path={REDIRECT_ROUTE} element={<AuthRedirectPage />} />
                        <Route path={VIDEO_PLAYER_ROUTE} element={<VideoPlayerPage />} />
                        <Route
                            path={TENANTS_ROUTE}
                            element={
                                <ProtectedRoute>
                                    <TenantSelectionPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path={PAGE_ROUTE}
                            element={
                                <ProtectedRoute>
                                    <ContentfulContext.Provider value={{ contentfulData }}>
                                        <ToastsContext.Provider
                                            value={{ toasts, addToast: handleAddToast, deleteToast: handleDeleteToast }}
                                        >
                                            <ToastsDispatchContext.Provider value={dispatch}>
                                                <AmpyChatProvider>
                                                    <MainTemplate hasBanner={hasBanner}>
                                                        <AppRoutes />
                                                        <Toaster />
                                                    </MainTemplate>
                                                </AmpyChatProvider>
                                            </ToastsDispatchContext.Provider>
                                        </ToastsContext.Provider>
                                    </ContentfulContext.Provider>
                                </ProtectedRoute>
                            }
                        />
                        <Route path={NOT_FOUND_ROUTE} element={<NotFoundPage />} />
                    </Routes>
                </div>
            </BrowserRouter>
        </SWRConfig>
    );
};

export default App;
